import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import Icon from './Icon';
import SEO from './SEO';
import './SupportPage.css';

const AUDIENCES = [
  { key: 'customer', label: 'Customer', theme: 'teal' },
  { key: 'rider', label: 'Rider', theme: 'teal' },
  { key: 'merchant', label: 'Merchant', theme: 'amber' },
];

const REQUEST_TYPES = [
  {
    key: 'enquiry',
    label: 'General Enquiry',
    icon: 'helpCircle',
    hint: 'Ask a question or tell us what happened. Our support team will get back to you.',
    placeholder: 'Tell us what you need help with…',
  },
  {
    key: 'deletion',
    label: 'Delete My Account',
    icon: 'trash',
    hint: "Let us know why you'd like your account deleted. We'll verify your identity and start the deletion process. Your account gets a 30 day grace period before anything is permanently removed.",
    placeholder: 'Tell us why you\'d like to delete your account, and any details that help us find it (registered phone/email)…',
  },
  {
    key: 'restoration',
    label: 'Restore My Account',
    icon: 'refresh',
    hint: "If your account is within its 30 day deletion window, we can restore it. Tell us your account details so support can find and reactivate it.",
    placeholder: 'Tell us which account you\'d like restored and any details that help us verify it\'s yours…',
  },
];

const submitWebSupportRequest = httpsCallable(functions, 'submitWebSupportRequest');

const AUDIENCE_FROM_PARAM = { customer: 'customer', rider: 'rider', merchant: 'merchant' };

export default function SupportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialAudience = AUDIENCE_FROM_PARAM[searchParams.get('for')] || 'customer';
  const [audience, setAudience] = useState(initialAudience);
  const [requestType, setRequestType] = useState('enquiry');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && navigate('/');
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const activeType = REQUEST_TYPES.find((t) => t.key === requestType);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) return setError('Please enter your name.');
    if (!email.trim() && !phone.trim()) return setError('Please provide an email or phone number so we can reach you.');
    if (!message.trim()) return setError('Please describe your request.');

    setSubmitting(true);
    try {
      await submitWebSupportRequest({
        app: audience,
        type: requestType,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="support-page">
      <SEO
        title="Contact BikesDrop Support | Customer, Rider & Merchant Help"
        description="Reach the BikesDrop team for a question, an issue with your account, or to delete or restore your account. We reply within 24 hours."
        path="/support/"
      />
      <div className="support-page__header">
        <div className="container support-page__header-inner">
          <Link className="support-page__back" to="/">
            <Icon name="arrowLeft" size={18} />
            Back to site
          </Link>
          <span className="support-page__eyebrow">
            <Icon name="mail" size={14} />
            Support
          </span>
        </div>
      </div>

      <div className="support-page__audiencebar">
        <div className="container support-page__audiencebar-inner">
          {AUDIENCES.map((a) => (
            <button
              key={a.key}
              className={`support-page__audience support-page__audience--${a.theme} ${
                audience === a.key ? 'support-page__audience--active' : ''
              }`}
              onClick={() => setAudience(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <div className="container support-page__body">
        {done ? (
          <div className="support-page__success">
            <div className="support-page__success-icon">
              <Icon name="check" size={30} />
            </div>
            <h1>Request received</h1>
            <p>
              Thanks, {name.split(' ')[0]}. Our support team will reach out to you
              {email ? ` at ${email}` : ''}
              {email && phone ? ' or' : ''}
              {phone ? ` by phone at ${phone}` : ''} within 24 hours.
            </p>
            <Link className="btn btn--teal" to="/">
              Back to site
            </Link>
            <button className="support-page__another" onClick={() => setDone(false)}>
              Send another request
            </button>
          </div>
        ) : (
          <>
            <div className="support-page__intro">
              <h1>Talk to BikesDrop Support</h1>
              <p>
                Reach our team about your {audience === 'customer' ? 'customer' : audience === 'rider' ? 'rider' : 'merchant'} account
. Questions, issues, or a request to delete or restore your account.
              </p>
            </div>

            <div className="support-page__types">
              {REQUEST_TYPES.map((t) => (
                <button
                  key={t.key}
                  className={`support-page__type ${requestType === t.key ? 'support-page__type--active' : ''}`}
                  onClick={() => setRequestType(t.key)}
                >
                  <Icon name={t.icon} size={16} />
                  {t.label}
                </button>
              ))}
            </div>

            <p className="support-page__hint">{activeType.hint}</p>

            <form className="support-page__form" onSubmit={handleSubmit}>
              <div className="support-page__row">
                <label className="support-page__field">
                  <span>Full name</span>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
                </label>
              </div>
              <div className="support-page__row support-page__row--two">
                <label className="support-page__field">
                  <span>Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                </label>
                <label className="support-page__field">
                  <span>Phone</span>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+233 …" />
                </label>
              </div>
              <label className="support-page__field">
                <span>Message</span>
                <textarea
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={activeType.placeholder}
                />
              </label>

              {error && <p className="support-page__error">{error}</p>}

              <button type="submit" className="btn btn--teal support-page__submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send to BikesDrop Support'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
